'use client'

import { useHeaderFooter } from '@cdc-internal/mkt-header-footer'
import { AppShell, AppShellHeader, AppShellMain, Box, Container } from '@mantine/core'
import { useParams } from 'next/navigation'
import { useEffect, useRef, useState } from 'react'

import { fiatCurrencies } from '@/configs/currencies'
import { isProductionAppEnv } from '@/configs/env'
import { useSsrContext } from '@/contexts/ssr'
import { useIsResponsiveMobileView } from '@/hooks/responsive'
import useDir from '@/hooks/useDir'
import { useBasePath, useI18nRouter } from '@/i18n/navigation'
import { setSelectedCurrency, setUserLocale } from '@/server/actions'

interface AppLayoutProps {
  locale: string
  children: React.ReactNode
  countryCode: string
}

function useHeaderHeight() {
  const headerRef = useRef<HTMLDivElement>(null)
  const [headHeight, setHeadHeight] = useState(54)
  /**
   * Observe header change, when it's childlist change, sync the real header's height to layout
   */
  useEffect(() => {
    if (!headerRef.current) return
    // TODO: Head have problem, if we observe it and setHeadHeight, add back the logic, after fixed it.
    // const mutationObserver = new MutationObserver(() => {
    //   const realHeaderElement = headerRef.current?.getElementsByTagName('header')[0]
    //   if (!realHeaderElement) return
    //   // Should
    //   // setHeadHeight(realHeaderElement.clientHeight)
    // })
    const realHeaderElement = headerRef.current?.getElementsByTagName('header')[0]
    if (!realHeaderElement) return
    setHeadHeight(realHeaderElement.clientHeight)
    // mutationObserver.observe(headerRef.current, { childList: true, subtree: true })
    return () => {
      // mutationObserver.disconnect()
    }
  }, [])
  return {
    headHeight,
    headerRef,
  }
}

export default function AppLayout({ children, locale, countryCode }: AppLayoutProps) {
  const { selectedCurrency } = useSsrContext()
  const basePath = useBasePath()
  const { locale: pathLocale } = useParams<{ locale: string }>()
  const is18nRouter = useI18nRouter()
  const isResponsiveMobileView = useIsResponsiveMobileView()
  const { headerRef, headHeight } = useHeaderHeight()

  const pathParts = basePath.split('/').filter(Boolean)

  const isCurrencyPage = pathParts.length >= 3
  const pathCurrency = isCurrencyPage ? pathParts[2].toUpperCase() : null
  const isValidPathCurrency = pathCurrency && fiatCurrencies.some(c => c.code === pathCurrency)
  const displayCurrency = isValidPathCurrency ? pathCurrency : selectedCurrency

  const { Header, Footer } = useHeaderFooter({
    environment: isProductionAppEnv ? 'production' : 'staging',
    isMobile: isResponsiveMobileView,
    countryCode,
    locale: pathLocale,
    language: locale,
    currency: displayCurrency,
    onClickCurrency: async currency => {
      await setSelectedCurrency(currency)
      if (isValidPathCurrency) {
        is18nRouter.replace(`/${pathParts[1]}/${currency.toLowerCase()}`)
      }
    },
    isAuthCheckRequired: false,
    onClickLanguageRegion: async newLocale => {
      is18nRouter.replace(basePath, { locale: newLocale })

      await setUserLocale(newLocale)
    },
    source: 'cdc-price',
  })
  const dir = useDir()
  return (
    <AppShell header={{ height: headHeight }}>
      <AppShellHeader dir={dir} ref={headerRef}>
        <Header />
      </AppShellHeader>

      <AppShellMain>
        {/* Main Content Area */}
        <Container
          size={1312}
          px="sm"
          pt="40px"
          // Minimum height of the main content area will fill the viewport height except the header
          mih="calc(100dvh - var(--app-shell-header-height))"
          display={'flex'}
          style={{ flexDirection: 'column' }}
        >
          {children}
        </Container>

        <Box dir={dir}>
          <Footer />
        </Box>
      </AppShellMain>
    </AppShell>
  )
}
